import React from "react";
import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/styles.min.css";
import { ChevronLeft, ChevronRight, ShoppingBag } from "lucide-react";
import { cn } from "@/lib/utils";
import { useImageErrorHandling } from "@/lib/image-utils";
import { useProductDetail } from "../hooks/use-product-detail";

/**
 * Product Images Section
 *
 * ✅ Zero Props - Self-contained
 * ✅ Shared between Modal and Page
 *
 * Features:
 * - Main image with zoom
 * - Image navigation (prev/next)
 * - Thumbnail gallery
 */

export const ProductImagesSection: React.FC = () => {
  const { product, activeImageIdx, onPrevImage, onNextImage, onImageClick } =
    useProductDetail();

  // Use image error handling utility
  const { handleImageError, isImageFailed } = useImageErrorHandling();

  const hasMultipleImages = product.images && product.images.length > 1;

  // Get current image with fallback handling
  const currentImage = product.images?.[activeImageIdx];
  const hasValidImage = currentImage && !isImageFailed(currentImage);

  return (
    <div className="flex flex-col gap-2 sm:gap-3">
      {/* Main Image */}
      <div className="relative rounded-xl sm:rounded-2xl overflow-hidden bg-muted aspect-square">
        {hasValidImage ? (
          <InnerImageZoom
            src={currentImage}
            zoomSrc={currentImage}
            zoomType="hover"
            zoomScale={1.1}
            className="w-full h-full object-cover"
            hideHint={false}
            fadeDuration={300}
            zoomPreload={true}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <ShoppingBag className="h-12 w-12 sm:h-16 sm:w-16" />
          </div>
        )}

        {/* Navigation Arrows */}
        {hasMultipleImages && (
          <>
            <button
              onClick={onPrevImage}
              className="absolute left-1.5 sm:left-2 top-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 border border-border-secondary dark:border-gray-600 rounded-full p-1.5 sm:p-1 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
              aria-label="Previous image"
            >
              <ChevronLeft className="size-3.5 sm:size-4 text-text-secondary dark:text-text-secondary" />
            </button>
            <button
              onClick={onNextImage}
              className="absolute right-1.5 sm:right-2 top-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 border border-border-secondary dark:border-gray-600 rounded-full p-1.5 sm:p-1 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
              aria-label="Next image"
            >
              <ChevronRight className="size-3.5 sm:size-4 text-text-secondary dark:text-text-secondary" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {hasMultipleImages && (
        <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-1 scrollbar-hide">
          {product.images.slice(0, 5).map((src: string, idx: number) => {
            const isFailedThumbnail = isImageFailed(src);
            return (
              <button
                key={`thumb-${idx}`}
                onClick={() => onImageClick(idx)}
                className={cn(
                  "relative flex-shrink-0 rounded-lg sm:rounded-xl overflow-hidden border-2 transition-all",
                  idx === activeImageIdx
                    ? "border-text-primary dark:border-white"
                    : "border-transparent hover:border-border-tertiary dark:hover:border-border-tertiary",
                  "w-[calc((100%-16px)/5)] sm:w-[calc((100%-24px)/5)] aspect-square"
                )}
                aria-label={`View image ${idx + 1}`}
              >
                {!isFailedThumbnail ? (
                  <img
                    src={src}
                    alt={`${product.title} - ${idx + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={() => handleImageError(src)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground">
                    <ShoppingBag className="h-3 w-3 sm:h-4 sm:w-4" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
